import { createSelector } from '@reduxjs/toolkit';
import { cloneDeep } from 'es-toolkit';

import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import { CachedRecentSearch, postDeleteSavedSearch, postSaveSearch } from '@/redux/api/saveSearch';
import { LOG_OUT_BIDDER } from '@/redux/modules/account/logout/logout.actions';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';
import { SearchParams } from '@/types/SaveSearch';
import { trackSearchFollowedAnalytics, trackSearchUnfollowedAnalytics } from '@/utils/amplitude/analytics';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { cleanKeywordSearchString } from '@/utils/cleanSearchTerms';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { toFacetUrlParams } from '@/utils/savedSearchFacets';
import { addOrsBetweenKeywords, sanitizeSearchKey } from '@/utils/strings';

import {
    DELETE_SAVE_SEARCH_FAIL,
    DELETE_SAVE_SEARCH_REQUEST,
    DELETE_SAVE_SEARCH_SUCCESS,
    LOAD_SAVED_SEARCHES_SUCCESS,
    SAVE_SEARCH_FAIL,
    SAVE_SEARCH_REQUEST,
    SAVE_SEARCH_SUCCESS,
    SET_SIGNUP_SAVE_SEARCH_PARAMS,
} from './actions';
import { getDeployment } from './config';
import { fetchSavedSearchesIfNeeded } from './savedSearch';
import { fetchRecentSearches } from './saveSearch.actions';
import { getRecentSearchQuery, SaveSearchParamsOrKey } from './saveSearch.helpers';

export { fetchRecentSearches, submitRecentSearch } from './saveSearch.actions';
export type { SubmitRecentSearchParams } from './saveSearch.actions';
export { getRecentSearchQuery, isKeywordInRecentSearches, type SaveSearchParamsOrKey } from './saveSearch.helpers';

const HEADER_RECENT_SEARCHES_DISPLAY_COUNT = 5;

export const defaultSaveSearchSlice: SaveSearchSlice = {
    byParams: {},
    error: false,
    newSignupSearchParams: {},
    recentSearches: [],
    recentSearchesLoaded: 0,
    recentSearchesLoading: false,
    //TODO remove these and update places using them to use byParams
    submitted: false,
    success: false,
};

type SavedSearch = {
    deleted?: boolean;
    key: string;
    newSignupSearchParams: SearchParams;
    savedSearchId?: number;
    submitted: boolean;
    success: boolean;
};

type ByParamsType = {
    [key: string]: SavedSearch;
};

export type SaveSearchSlice = {
    byParams: ByParamsType;
    error: boolean;
    newSignupSearchParams: {};
    recentSearches: CachedRecentSearch[];
    recentSearchesLoaded: number;
    recentSearchesLoading: boolean;
    submitted: boolean;
    success: boolean;
};

export default function reducer(state = defaultSaveSearchSlice, action: any = {}): SaveSearchSlice {
    const existing = cloneDeep(state.byParams);

    switch (action.type) {
        case fetchRecentSearches.pending.type:
            return {
                ...state,
                recentSearchesLoading: true,
            };
        case fetchRecentSearches.fulfilled.type:
            return {
                ...state,
                recentSearches: action.payload ?? [],
                recentSearchesLoaded: Date.now(),
                recentSearchesLoading: false,
            };
        case fetchRecentSearches.rejected.type:
            return {
                ...state,
                recentSearchesLoading: false,
            };
        case SAVE_SEARCH_REQUEST:
        case DELETE_SAVE_SEARCH_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case SAVE_SEARCH_SUCCESS:
            const sanitizedKey = sanitizeSearchKey(String(action.payload.key));
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${sanitizedKey}`]: {
                        ...existing[`${sanitizedKey}`],
                        deleted: false,
                        key: `${sanitizedKey}`,
                        savedSearchId: action.payload.id,
                    },
                },
                submitted: false,
                success: true,
            };
        case LOAD_SAVED_SEARCHES_SUCCESS:
            let byParams = {};
            action.payload?.forEach((search) => {
                const key = createSaveSearchKeyBySearchParams(toFacetUrlParams(search.facets));
                if (key) {
                    byParams = {
                        ...byParams,
                        [key]: {
                            deleted: false,
                            key,
                            savedSearchId: search.savedSearchId,
                        },
                    };
                }
            });
            return {
                ...state,
                byParams: { ...existing, ...byParams },
                submitted: false,
                success: true,
            };
        case LOG_OUT_BIDDER:
            return { ...defaultSaveSearchSlice };
        case DELETE_SAVE_SEARCH_SUCCESS:
            const deleteKey = action.payload;
            return {
                ...state,
                byParams: {
                    ...existing,
                    [deleteKey]: {
                        ...existing[deleteKey],
                        deleted: true,
                    },
                },
                error: false,
                submitted: false,
                success: true,
            };
        case DELETE_SAVE_SEARCH_FAIL:
        case SAVE_SEARCH_FAIL:
            let newByParams = cloneDeep(existing);
            if (action.meta?.key) {
                newByParams[`${action.meta.key}`] = {
                    ...existing[`${action.meta.key}`],
                };
            }
            return {
                ...state,
                byParams: newByParams,
                error: true,
                submitted: false,
                success: false,
            };
        case SET_SIGNUP_SAVE_SEARCH_PARAMS:
            return {
                ...state,
                newSignupSearchParams: action.payload,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;
export const getSaveSearchState = createSelector(stateSelector, (state) => state.saveSearch);

const idSelector = (_: GlobalState, id: number | string) => id;

const byParamsSelector = createSelector(getSaveSearchState, (state) => state.byParams);

export const getNewSignupSearchParams = createSelector(getSaveSearchState, (state) => state.newSignupSearchParams);
export const getSaveSearchSubmitted = createSelector(getSaveSearchState, (state) => state.submitted);
export const getSaveSearchSuccess = createSelector(getSaveSearchState, (state) => state.success);

/**
 *
 * @param queryParams SearchUrlParams usually sourced from parseQueryParams
 * @param pathname string pathname to parse categories from, if category or priceguide page for additional category_id parsing
 * @param cleanAndFilter boolean to clean and filter the keyword param
 * @returns SearchUrlParams
 */
export const getSaveSearchParams = (
    queryParams: SearchUrlParams,
    pathname: string = '',
    cleanAndFilter: boolean = false
): SearchUrlParams | null => {
    const MAX_WORDS = 4;
    const newParams = filterSaveSearchParams(queryParams);

    if (newParams?.keyword) {
        newParams.keyword = cleanAndFilter
            ? addOrsBetweenKeywords(cleanKeywordSearchString(newParams.keyword), MAX_WORDS)
            : newParams.keyword;
    } else {
        delete newParams.keyword;
    }

    if ((pathname.includes('/c/') || pathname.includes('/price-guide/')) && !queryParams.campaign_id) {
        delete newParams.keyword;
        let pathCategoryIds: string[] = [];
        const categoryIdMatches = pathname.matchAll(/\/(\d+)/g);
        for (const categoryId of categoryIdMatches) {
            pathCategoryIds.push(categoryId[1]);
        }
        newParams.category_id = [...pathCategoryIds, newParams.category_id].filter((i) => Boolean(i)).join(',');
    }

    if (pathname.includes('/price-result/') && !queryParams.category_id) {
        newParams.keyword = typeof document !== 'undefined' ? document?.title : '';
    }

    return Object.keys(newParams).length ? newParams : null;
};

/**
 * filterSaveSearchParams - filters given queryParams object and returns SearchUrlParams with allowed
 * parameters only for a Save Search
 * @param queryParams SearchUrlParams
 * @returns SearchUrlParams
 */
const filterSaveSearchParams = (queryParams: SearchUrlParams) => {
    const safeSearchFilters = [
        'category_id',
        'country',
        'current_bid',
        'distance',
        'estimated_price',
        'house_id',
        'keyword',
        'location',
        'payment',
        'sale_type',
        'shipping',
        'topRated',
    ];
    let safeSaveSearchParams: SearchUrlParams = {};
    Object.keys(queryParams).forEach((queryKey) => {
        if (safeSearchFilters.includes(queryKey)) {
            safeSaveSearchParams[queryKey] = queryParams[queryKey];
        }
    });
    return safeSaveSearchParams;
};

export const createSaveSearchParams = (categoryId: string) => {
    return {
        category_id: categoryId,
    };
};

export const createSaveSearchKey = (categoryId: string) => {
    return createSaveSearchKeyBySearchParams(createSaveSearchParams(categoryId));
};

export const createSaveSearchKeyBySearchParams = (searchParams: SearchParams): string => {
    if (!searchParams) {
        return '';
    }
    const sortedParams = {};
    Object.keys(searchParams)
        .sort()
        .forEach((key: string) => {
            if (key === 'status') {
                return;
            }
            sortedParams[key] =
                (typeof searchParams?.[key] === 'string' ? searchParams[key] : '').replace(/\s+/g, ' ').trim() ||
                sortedParams[key];
        });

    const searchKey = sanitizeSearchKey(JSON.stringify(sortedParams).replace(/}|{|"/g, ''));
    return searchKey;
};

export const getHasSavedSearches = createSelector(byParamsSelector, (byParams) => {
    const savedSearches = Object.keys(byParams).filter((key) => {
        return !byParams[key].deleted;
    });
    return Boolean(savedSearches.length);
});

export const getSavedSearchesCount = createSelector(byParamsSelector, (byParams) => {
    const savedSearches = Object.keys(byParams).filter((key) => !byParams[key].deleted);
    return savedSearches.length;
});

export const getIsSavedSearch = createSelector([byParamsSelector, idSelector], (byParams, saveSearchKey) => {
    const sanitizedSaveSearchKey = sanitizeSearchKey(String(saveSearchKey));
    const savedSearch = Boolean(byParams[sanitizedSaveSearchKey] && !byParams[sanitizedSaveSearchKey].deleted);
    return savedSearch;
});

export const getSavedSearchId = createSelector([byParamsSelector, idSelector], (byParams, saveSearchKey) => {
    const sanitizedSaveSearchKey = sanitizeSearchKey(String(saveSearchKey));
    return byParams[sanitizedSaveSearchKey]?.savedSearchId || 0;
});

export const getSavedSearchKeyById = createSelector([byParamsSelector, idSelector], (byParams, id) => {
    const savedSearchArray = Object.keys(byParams);
    let savedSearchKey = '';
    savedSearchArray.forEach((key) => {
        if (byParams[key].savedSearchId === Number(id)) {
            savedSearchKey = byParams[key].key;
        }
    });

    return savedSearchKey;
});

export const getRecentSearchesLoading = createSelector(getSaveSearchState, (state) => state.recentSearchesLoading);
export const getRecentSearchesLoaded = createSelector(getSaveSearchState, (state) => state.recentSearchesLoaded);

export type HeaderRecentSearch = {
    query: string;
    url: string;
};

export const getHeaderRecentSearches = createSelector(getSaveSearchState, (saveSearch): HeaderRecentSearch[] =>
    saveSearch.recentSearches.slice(0, HEADER_RECENT_SEARCHES_DISPLAY_COUNT).flatMap((recentSearch) => {
        const query = getRecentSearchQuery(recentSearch);

        if (!query) {
            return [];
        }

        return [{ query, url: recentSearch.url }];
    })
);

/* ACTION CREATORS */
export const submitSaveSearch =
    (saveSearchParams: SaveSearchParamsOrKey, sendAlert: boolean, itemId?: number) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const savedSearchKey =
                typeof saveSearchParams !== 'string'
                    ? createSaveSearchKeyBySearchParams(saveSearchParams)
                    : saveSearchParams;

            dispatch({
                payload: savedSearchKey,
                type: SAVE_SEARCH_REQUEST,
            });
            const { payload } = await postSaveSearch({
                authToken,
                deployment,
                saveSearchParams,
                sendAlert,
            });
            dispatch({
                error: false,
                payload: {
                    id: payload,
                    key: savedSearchKey,
                },
                type: SAVE_SEARCH_SUCCESS,
            });
            dispatch(fetchSavedSearchesIfNeeded(true));

            const analyticsKeyword = typeof saveSearchParams !== 'string' ? saveSearchParams.keyword : saveSearchParams;
            trackSearchFollowedAnalytics({
                itemId: itemId,
                pageName: getAmplitudePageNameFromPath(window.location.pathname),
                searchKeywords: analyticsKeyword,
                url: getFullUrl(deployment, window.location.pathname),
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: {
                    key:
                        typeof saveSearchParams !== 'string'
                            ? createSaveSearchKeyBySearchParams(saveSearchParams)
                            : saveSearchParams,
                },
                payload: error,
                type: SAVE_SEARCH_FAIL,
            });
        }
    };

export const deleteSavedSearch =
    (saveSearchParams: SaveSearchParamsOrKey) => async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const saveSearchKey =
                typeof saveSearchParams !== 'string'
                    ? createSaveSearchKeyBySearchParams(saveSearchParams)
                    : sanitizeSearchKey(saveSearchParams);
            const savedSearchId = getSavedSearchId(state, saveSearchKey);

            dispatch({
                payload: saveSearchKey,
                type: DELETE_SAVE_SEARCH_REQUEST,
            });

            await postDeleteSavedSearch({
                authToken,
                deployment,
                savedSearchId,
            });
            dispatch({
                payload: saveSearchKey,
                type: DELETE_SAVE_SEARCH_SUCCESS,
            });

            trackSearchUnfollowedAnalytics({
                pageName: getAmplitudePageNameFromPath(window.location.pathname),
                url: getFullUrl(deployment, window.location.pathname),
            });

            return dispatch(fetchSavedSearchesIfNeeded(true));
        } catch (error) {
            return dispatch({
                error: true,
                payload: error,
                type: DELETE_SAVE_SEARCH_FAIL,
            });
        }
    };

export const setNewSignupSearchParams = (params: SearchParams) => async (dispatch: AppDispatch) => {
    return dispatch({
        payload: params,
        type: SET_SIGNUP_SAVE_SEARCH_PARAMS,
    });
};

/**
 * After login with saveSearch in URL: load saved searches, then only submit if this search isn't already saved.
 * Prevents duplicate saved search when user lands from alert email and logs in.
 */
export const submitSaveSearchAfterLoginIfNeeded =
    (queryParams: SearchUrlParams, pathname: string = '') =>
    async (dispatch: AppDispatch, getState: AppGetState): Promise<{ didSave: boolean }> => {
        const ssParams = getSaveSearchParams(queryParams, pathname);
        if (!ssParams) {
            return { didSave: false };
        }
        await dispatch(fetchSavedSearchesIfNeeded(true));
        const state = getState();
        const key = createSaveSearchKeyBySearchParams(ssParams);
        if (getIsSavedSearch(state, key)) {
            return { didSave: false };
        }
        await dispatch(submitSaveSearch(ssParams, true));
        return { didSave: true };
    };
